import { CollectedSkuStat } from '@/pages/home/ui/collected-sku-stat';
import { VariantCollecton, useVariantCollectionQuery } from '@/shared/api';
import { useAppSelector } from '@/shared/hooks';
import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { ScrollArea, Separator, Text } from '@radix-ui/themes';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

export function InfoSidebar() {
  const selectedSku = useAppSelector((state) => state.collection.selectedSku);
  const bgImage = useAppSelector((state) => state.collection.backgroundImage);
  const selectedFrameIndex = useAppSelector(
    (state) => state.collection.selectedFrameIndex
  );
  const framesList = useAppSelector((state) => state.collection.framesList);

  return (
    <aside className="h-full flex flex-col">
      {selectedSku ? (
        <SKUInfo sku={selectedSku} />
      ) : bgImage || selectedFrameIndex > -1 || framesList.length > 0 ? (
        <CollectionInfo />
      ) : (
        <div className="h-full flex items-center justify-center text-gray-400 text-sm">
          No data to display
        </div>
      )}
    </aside>
  );
}

function CollectionInfo() {
  const bgImage = useAppSelector((state) => state.collection.backgroundImage);
  const lastFrameImage = useAppSelector(
    (state) =>
      state.collection.framesList[
        state.collection.selectedFrameIndex < 0
          ? state.collection.framesList.length - 1
          : state.collection.selectedFrameIndex
      ]?.image
  );

  return (
    <>
      <div className="p-3 border-b">
        <Text as="p" className="font-medium mb-1">
          Last captured frame:
        </Text>
        <figure className="aspect-video bg-gray-200">
          <img
            className="object-cover w-full h-full"
            src={lastFrameImage ? lastFrameImage : '/placeholder.svg'}
          />
        </figure>
      </div>
      <div className="p-3 border-b">
        <Text as="p" className="font-medium mb-1">
          Last captured background:
        </Text>
        <figure className="aspect-video bg-gray-200">
          <img src={bgImage || '/placeholder.svg'} />
        </figure>
      </div>
      <CollectedSkuStat />
    </>
  );
}

function SKUInfo({ sku }: { sku: string }) {
  const [sampleImage, setSampleImage] = useState<string | null>(null);

  const { data } = useVariantCollectionQuery(
    {
      sku,
    },
    {
      skip: !sku,
      refetchOnMountOrArgChange: true,
    }
  );

  const trainingData = data?.results.filter(
    (item) => item.dataset_type === 'training'
  );
  const evaluationData = data?.results.filter(
    (item) => item.dataset_type === 'validation'
  );

  const onSelect = (dataset: VariantCollecton | null) => {
    setSampleImage(dataset?.sample_image_url || null);
  };

  return (
    <>
      <div className="p-2 pb-0">
        <figure className="aspect-video bg-gray-200">
          <img
            src={sampleImage || '/placeholder.svg'}
            className="w-full h-full object-cover"
          />
        </figure>
        <Text as="p" className="font-medium mt-2">
          Collection samples:
        </Text>
      </div>
      <div className="flex flex-grow flex-col">
        <DatasetHistory
          title="Training datasets"
          data={trainingData || []}
          onClick={onSelect}
        />
        <Separator size="4" className="my-2 bg-gray-400" />
        <DatasetHistory
          onClick={onSelect}
          title="Evaluation datasets"
          data={evaluationData || []}
        />
      </div>
    </>
  );
}

function DatasetHistory({
  data,
  title,
  onClick,
}: {
  data: VariantCollecton[];
  title: string;
  onClick?: (item: VariantCollecton | null) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const total = data.reduce<{
    containingImageCount: number;
    totalImageCount: number;
  }>(
    (acc, collection) => {
      acc.containingImageCount += collection.containing_image_count;
      acc.totalImageCount += collection.total_image_count;

      return acc;
    },
    { containingImageCount: 0, totalImageCount: 0 }
  );

  return (
    <ScrollArea className="p-2 flex-grow">
      <Text as="p" weight="bold" className="sticky top-0 mb-2 bg-white">
        {total.containingImageCount} / {total.totalImageCount} {title}
      </Text>
      <ul className="flex flex-col gap-1">
        {data.map((collection, index) => (
          <li
            key={collection.id}
            onClick={() => {
              if (collection.id === selected) {
                setSelected(null);
                onClick?.(null);
              } else {
                setSelected(collection.id);
                onClick?.(collection);
              }
            }}
            className={twMerge([
              'hover:bg-gray-100 cursor-pointer w-full rounded-lg gap-1 flex items-center border p-1 text-sm border-transparent hover:border-blue-400',
              selected === collection.id &&
                'bg-blue-100 border-blue-400 hover:bg-blue-200',
            ])}
          >
            <span>{data.length - index}.</span>
            <span className="font-bold whitespace-nowrap mr-2">
              {collection.containing_image_count} /{' '}
              {collection.total_image_count}
            </span>
            <span className="text-ellipsis overflow-hidden break-all line-clamp-1">
              {collection.name}
            </span>
            <a href="#">
              <ExternalLinkIcon />
            </a>
          </li>
        ))}
      </ul>
    </ScrollArea>
  );
}
